import { type SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';

import type { DraggableEvent } from 'react-draggable';

const ELEMENT_WIDTH = 718;
const ELEMENT_HEIGHT = 50;
const CATALOG_POSITION_Y = 130;
const DEFAULT_POSITION_Y = 180;

type SelectorPosition = { x: number; y: number };

type UseSymbolPositionOptions = {
  symbolCount: number;
  isInCatalog: boolean;
  selectionKey: string;
};

export const useSymbolPosition = ({
  symbolCount,
  isInCatalog,
  selectionKey
}: UseSymbolPositionOptions) => {
  const calculateDefaultPosition = useCallback((): SelectorPosition => {
    const x = (window.innerWidth - ELEMENT_WIDTH) / 2;
    const y = isInCatalog ? window.innerHeight - CATALOG_POSITION_Y : DEFAULT_POSITION_Y;
    return { x, y };
  }, [isInCatalog]);

  const defaultPosition = useMemo(() => {
    if (symbolCount === 0) return undefined;
    return calculateDefaultPosition();
  }, [calculateDefaultPosition, symbolCount]);

  const [positionState, setPositionState] = useState<{
    selectionKey: string;
    position: SelectorPosition | undefined;
  } | null>(null);

  const position = useMemo(() => {
    if (positionState?.selectionKey === selectionKey) return positionState.position;
    if (symbolCount === 0) return undefined;
    return defaultPosition;
  }, [defaultPosition, positionState, selectionKey, symbolCount]);

  const setPosition = useCallback(
    (update: SetStateAction<SelectorPosition | undefined>) => {
      setPositionState(prev => {
        const currentPosition =
          prev?.selectionKey === selectionKey ? prev.position : defaultPosition;
        const nextPosition = typeof update === 'function' ? update(currentPosition) : update;
        return { selectionKey, position: nextPosition };
      });
    },
    [defaultPosition, selectionKey]
  );

  const constrainToViewport = useCallback((pos: SelectorPosition): SelectorPosition => {
    const x = Math.max(0, Math.min(pos.x, window.innerWidth - ELEMENT_WIDTH));
    const y = Math.max(0, Math.min(pos.y, window.innerHeight - ELEMENT_HEIGHT));
    return { x, y };
  }, []);

  const handleResize = useCallback(() => {
    if (position) {
      setPosition(constrainToViewport(position));
    }
  }, [constrainToViewport, position, setPosition]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  const handleDrag = useCallback(
    (_: DraggableEvent, data: SelectorPosition) => {
      setPosition({ x: data.x, y: data.y });
    },
    [setPosition]
  );

  return { position, handleDrag };
};
